import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TrendingUp, Plus, Trash2, Clock, Receipt, PhoneCall, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Page";

const eur = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const today = () => new Date().toISOString().slice(0, 10);
const downloadBlob = async (url, filename) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const u = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = u; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(u);
  } catch (e) {
    if (e?.response?.status === 400) { try { const txt = JSON.parse(await e.response.data.text()); toast.error(txt.detail || "Export fehlgeschlagen"); return; } catch { /* noop */ } }
    toast.error("Export fehlgeschlagen");
  }
};

const marginStatus = (pct, threshold) => {
  if (threshold == null || pct == null) return null;
  if (pct < threshold) return { color: "bg-rose-500", label: `unter Zielmarge (${threshold}%)` };
  if (pct < threshold + 5) return { color: "bg-amber-400", label: "knapp an der Zielmarge" };
  return { color: "bg-emerald-500", label: "im Ziel" };
};
const Ampel = ({ pct, threshold }) => {
  const s = marginStatus(pct, threshold);
  if (!s) return null;
  return <span title={s.label} className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${s.color}`} data-testid="margin-ampel" />;
};

export default function Margin() {
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [pForm, setPForm] = useState({ customer_id: "", project_no: "", title: "", description: "", status: "offen" });
  const [detail, setDetail] = useState(null);
  const [threshold, setThreshold] = useState(null);

  const load = async () => {
    try {
      const [p, c, e, co] = await Promise.all([api.get("/projects"), api.get("/customers"), api.get("/employees"), api.get("/company")]);
      setProjects(p.data || []); setCustomers(c.data || []); setEmployees((e.data || []).filter((u) => u.role === "employee"));
      const th = co.data?.margin_threshold_pct;
      setThreshold(th == null || th === "" ? null : Number(th));
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = async () => {
    setPForm({ customer_id: customers[0]?.id || "", project_no: "", title: "", description: "", status: "offen" });
    try { const r = await api.get("/projects/suggest-no"); setPForm((f) => ({ ...f, project_no: r.data.project_no })); } catch { /* noop */ }
    setAddOpen(true);
  };
  const createProject = async () => {
    if (!pForm.customer_id) return toast.error("Bitte Kunde wählen");
    if (!pForm.project_no.trim() || !pForm.title.trim()) return toast.error("Projektnummer und Titel erforderlich");
    try { await api.post("/projects", pForm); toast.success("Projekt angelegt"); setAddOpen(false); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const delProject = async (id) => { try { await api.delete(`/projects/${id}`); toast.success("Projekt gelöscht"); load(); } catch (e) { toast.error(apiError(e)); } };
  const openDetail = async (id) => { try { const r = await api.get(`/projects/${id}`); setDetail(r.data); } catch (e) { toast.error(apiError(e)); } };
  const reloadDetail = async () => { if (detail) { try { const r = await api.get(`/projects/${detail.id}`); setDetail(r.data); load(); } catch { /* noop */ } } };

  return (
    <div>
      <PageHeader title="Margin Report" subtitle="Kosten & Einnahmen pro Projekt – so siehst du die echte Marge. Nur für Admins sichtbar." testid="margin-header"
        action={<Button onClick={openAdd} data-testid="project-new" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Projekt</Button>} />

      {threshold != null && (
        <div className="flex items-center gap-4 text-xs text-slate-500 mb-3" data-testid="margin-legend">
          <span className="font-medium text-slate-600">Zielmarge {threshold}%:</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> im Ziel</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> knapp</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" /> unter Ziel</span>
        </div>
      )}

      {projects.length === 0 ? (
        <Card className="border-slate-200 rounded-xl"><EmptyState icon={TrendingUp} title="Noch keine Projekte" subtitle="Lege ein Projekt an und buche Kosten/Einnahmen darauf." testid="margin-empty" /></Card>
      ) : (
        <div className="grid gap-3">
          {projects.map((p) => {
            const t = p.totals || {};
            return (
              <Card key={p.id} className="p-4 border-slate-200 rounded-xl hover:border-slate-300 cursor-pointer" data-testid={`project-${p.id}`} onClick={() => openDetail(p.id)}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-slate-100 rounded px-2 py-0.5">{p.project_no}</span>
                      <span className="font-semibold text-slate-900 truncate">{p.title}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{p.customer_name} · {t.hours || 0} h gebucht</div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right"><div className="text-[11px] text-slate-400">Umsatz</div><div className="font-semibold text-slate-700">{eur(t.revenue)}</div></div>
                    <div className="text-right"><div className="text-[11px] text-slate-400">Kosten</div><div className="font-semibold text-slate-700">{eur(t.cost)}</div></div>
                    <div className="text-right"><div className="text-[11px] text-slate-400">Marge</div><div className="flex items-center justify-end gap-1.5"><Ampel pct={t.margin_pct} threshold={threshold} /><span className={`font-bold ${t.margin >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`project-margin-${p.id}`}>{eur(t.margin)}{t.margin_pct != null ? ` · ${t.margin_pct}%` : ""}</span></div></div>
                    <button onClick={(ev) => { ev.stopPropagation(); delProject(p.id); }} data-testid={`project-delete-${p.id}`} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="project-dialog">
          <DialogHeader><DialogTitle>Neues Projekt</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-600">Kunde</label>
              <Select value={pForm.customer_id} onValueChange={(v) => setPForm({ ...pForm, customer_id: v })}>
                <SelectTrigger data-testid="project-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_no} · {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="project-no" placeholder="Projektnummer" value={pForm.project_no} onChange={(e) => setPForm({ ...pForm, project_no: e.target.value })} />
              <Input data-testid="project-status" placeholder="Status" value={pForm.status} onChange={(e) => setPForm({ ...pForm, status: e.target.value })} />
            </div>
            <Input data-testid="project-title" placeholder="Titel *" value={pForm.title} onChange={(e) => setPForm({ ...pForm, title: e.target.value })} />
            <Input data-testid="project-desc" placeholder="Beschreibung" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} />
          </div>
          <DialogFooter><Button onClick={createProject} data-testid="project-save" className="brand-bg text-white hover:opacity-90">Anlegen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && <DetailDialog detail={detail} employees={employees} threshold={threshold} onClose={() => { setDetail(null); load(); }} reload={reloadDetail} />}
    </div>
  );
}

function DetailDialog({ detail, employees, threshold, onClose, reload }) {
  const t = detail.totals || {};
  const [tf, setTf] = useState({ user_id: employees[0]?.user_id || "", date: today(), hours: "", cost_rate: "", sell_rate: "", note: "" });
  const [itf, setItf] = useState({ kind: "cost", label: "", amount: "", date: today(), note: "" });
  const [ef, setEf] = useState({ date: today(), minutes: "", amount: "", billable: false, note: "" });

  const addTime = async () => {
    if (!tf.user_id || !tf.hours) return toast.error("Mitarbeiter und Stunden erforderlich");
    try { await api.post(`/projects/${detail.id}/time`, { ...tf, hours: parseFloat(tf.hours), cost_rate: parseFloat(tf.cost_rate || 0), sell_rate: parseFloat(tf.sell_rate || 0) }); setTf({ ...tf, hours: "", note: "" }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const addItem = async () => {
    if (!itf.label.trim() || !itf.amount) return toast.error("Bezeichnung und Betrag erforderlich");
    try { await api.post(`/projects/${detail.id}/items`, { ...itf, amount: parseFloat(itf.amount) }); setItf({ ...itf, label: "", amount: "", note: "" }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const addEffort = async () => {
    if (!ef.minutes && !ef.amount) return toast.error("Dauer oder Betrag erforderlich");
    try { await api.post(`/projects/${detail.id}/efforts`, { ...ef, minutes: parseInt(ef.minutes || 0, 10), amount: parseFloat(ef.amount || 0) }); setEf({ ...ef, minutes: "", amount: "", note: "", billable: false }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const delX = async (url) => { try { await api.delete(url); reload(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><span className="font-mono text-xs bg-slate-100 rounded px-2 py-0.5">{detail.project_no}</span> {detail.title}</DialogTitle></DialogHeader>
        <div className="flex justify-end gap-2 -mt-2">
          <Button size="sm" variant="outline" onClick={() => downloadBlob(`/projects/${detail.id}/export.csv`, `Projekt_${detail.project_no}.csv`)} data-testid="project-export"><Download className="h-4 w-4 mr-1" /> CSV-Nachweis</Button>
          <Button size="sm" variant="outline" onClick={() => downloadBlob(`/projects/${detail.id}/efforts-receipt.pdf`, `Zusatzaufwaende_${detail.project_no}.pdf`)} data-testid="project-pdf" className="text-sky-700 border-sky-200 hover:bg-sky-50"><FileText className="h-4 w-4 mr-1" /> PDF-Beleg</Button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center"><div className="text-xs text-slate-400">Umsatz</div><div className="font-bold text-slate-800">{eur(t.revenue)}</div></div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center"><div className="text-xs text-slate-400">Kosten</div><div className="font-bold text-slate-800">{eur(t.cost)}</div></div>
          <div className={`rounded-xl p-3 text-center border ${t.margin >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}><div className="text-xs text-slate-400">Marge</div><div className="flex items-center justify-center gap-1.5"><Ampel pct={t.margin_pct} threshold={threshold} /><span className={`font-bold ${t.margin >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="detail-margin">{eur(t.margin)}{t.margin_pct != null ? ` · ${t.margin_pct}%` : ""}</span></div></div>
        </div>
        {t.billable_amount > 0 && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">Abrechenbare Zusatzaufwände: <strong>{eur(t.billable_amount)}</strong></div>}

        <Section icon={Clock} title="Stundenbuchungen">
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            <Select value={tf.user_id} onValueChange={(v) => setTf({ ...tf, user_id: v })}><SelectTrigger className="col-span-2" data-testid="time-emp"><SelectValue placeholder="Mitarbeiter" /></SelectTrigger><SelectContent>{employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent></Select>
            <Input type="date" value={tf.date} onChange={(e) => setTf({ ...tf, date: e.target.value })} data-testid="time-date" />
            <Input type="number" placeholder="Std." value={tf.hours} onChange={(e) => setTf({ ...tf, hours: e.target.value })} data-testid="time-hours" />
            <Input type="number" placeholder="€/h Kosten" value={tf.cost_rate} onChange={(e) => setTf({ ...tf, cost_rate: e.target.value })} data-testid="time-cost" />
            <Input type="number" placeholder="€/h Verkauf" value={tf.sell_rate} onChange={(e) => setTf({ ...tf, sell_rate: e.target.value })} data-testid="time-sell" />
          </div>
          <Button size="sm" onClick={addTime} data-testid="time-add" className="brand-bg text-white hover:opacity-90 mt-2"><Plus className="h-3.5 w-3.5 mr-1" /> Buchen</Button>
          <List rows={detail.times} render={(x) => `${fmtDate(x.date)} · ${x.user_name} · ${x.hours} h · ${eur(x.hours * x.cost_rate)} Kosten${x.sell_rate ? ` / ${eur(x.hours * x.sell_rate)} Verkauf` : ""} · gebucht ${(x.created_at || "").slice(0, 16).replace("T", " ")}`} onDel={(x) => delX(`/project-time/${x.id}`)} testid="time" />
        </Section>

        <Section icon={Receipt} title="Kosten & Einnahmen (manuell)">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Select value={itf.kind} onValueChange={(v) => setItf({ ...itf, kind: v })}><SelectTrigger data-testid="item-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cost">Kosten</SelectItem><SelectItem value="revenue">Einnahme</SelectItem></SelectContent></Select>
            <Input className="col-span-2" placeholder="Bezeichnung (z. B. Material)" value={itf.label} onChange={(e) => setItf({ ...itf, label: e.target.value })} data-testid="item-label" />
            <Input type="number" placeholder="€" value={itf.amount} onChange={(e) => setItf({ ...itf, amount: e.target.value })} data-testid="item-amount" />
            <Input type="date" value={itf.date} onChange={(e) => setItf({ ...itf, date: e.target.value })} data-testid="item-date" />
          </div>
          <Button size="sm" onClick={addItem} data-testid="item-add" className="brand-bg text-white hover:opacity-90 mt-2"><Plus className="h-3.5 w-3.5 mr-1" /> Hinzufügen</Button>
          <List rows={detail.items} render={(x) => `${fmtDate(x.date)} · ${x.kind === "cost" ? "Kosten" : "Einnahme"}: ${x.label} · ${eur(x.amount)}`} onDel={(x) => delX(`/project-items/${x.id}`)} testid="item" />
        </Section>

        <Section icon={PhoneCall} title="Zusatzaufwände (Telefonate, Nachbesserungen)">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Input type="date" value={ef.date} onChange={(e) => setEf({ ...ef, date: e.target.value })} data-testid="effort-date" />
            <Input type="number" placeholder="Min." value={ef.minutes} onChange={(e) => setEf({ ...ef, minutes: e.target.value })} data-testid="effort-min" />
            <Input type="number" placeholder="€ Kosten" value={ef.amount} onChange={(e) => setEf({ ...ef, amount: e.target.value })} data-testid="effort-amount" />
            <Input className="col-span-2" placeholder="Notiz" value={ef.note} onChange={(e) => setEf({ ...ef, note: e.target.value })} data-testid="effort-note" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 mt-2"><Checkbox checked={ef.billable} onCheckedChange={(v) => setEf({ ...ef, billable: !!v })} data-testid="effort-billable" /> abrechenbar</label>
          <Button size="sm" onClick={addEffort} data-testid="effort-add" className="brand-bg text-white hover:opacity-90 mt-2"><Plus className="h-3.5 w-3.5 mr-1" /> Erfassen</Button>
          <List rows={detail.efforts} render={(x) => `${fmtDate(x.date)} · ${x.minutes} Min · ${eur(x.amount)}${x.billable ? " · abrechenbar" : ""}${x.note ? ` · ${x.note}` : ""}`} onDel={(x) => delX(`/project-efforts/${x.id}`)} testid="effort" />
        </Section>
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      <div className="flex items-center gap-2 font-semibold text-slate-800 mb-2"><Icon className="h-4 w-4 text-slate-500" /> {title}</div>
      {children}
    </div>
  );
}

function List({ rows, render, onDel, testid }) {
  if (!rows || rows.length === 0) return <div className="text-xs text-slate-400 mt-2">Noch keine Einträge.</div>;
  return (
    <div className="mt-3 divide-y divide-slate-100 border border-slate-100 rounded-lg">
      {rows.map((x) => (
        <div key={x.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`${testid}-row-${x.id}`}>
          <span className="text-slate-700">{render(x)}</span>
          <button onClick={() => onDel(x)} className="text-slate-300 hover:text-rose-500 shrink-0" data-testid={`${testid}-del-${x.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
}
